import React, { useContext } from 'react';
import { AuthContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Shield, 
  Check, 
  X, 
  Crown,
  User,
  UserCheck,
  Users,
  Eye
} from 'lucide-react';
import { motion } from 'framer-motion';

const ROLES = [
  {
    id: 'director',
    name: 'Director',
    icon: Crown,
    color: 'bg-purple-500',
    description: 'Full access to all features including admin settings',
    permissions: {
      view_all_inspections: true,
      create_inspections: true,
      edit_all_inspections: true,
      delete_inspections: true,
      view_all_reports: true,
      create_reports: true,
      edit_all_reports: true,
      manage_users: true,
      manage_roles: true,
      manage_lookups: true,
      view_dashboard_stats: true,
      assign_surveyors: true
    }
  },
  {
    id: 'admin',
    name: 'Admin',
    icon: Shield,
    color: 'bg-red-500',
    description: 'Administrative access with user and settings management',
    permissions: {
      view_all_inspections: true,
      create_inspections: true,
      edit_all_inspections: true,
      delete_inspections: false,
      view_all_reports: true,
      create_reports: true,
      edit_all_reports: true,
      manage_users: true,
      manage_roles: false,
      manage_lookups: true,
      view_dashboard_stats: true,
      assign_surveyors: true
    }
  },
  {
    id: 'surveyor',
    name: 'Surveyor',
    icon: UserCheck,
    color: 'bg-blue-500',
    description: 'Standard surveyor with full inspection capabilities',
    permissions: {
      view_all_inspections: true,
      create_inspections: true,
      edit_all_inspections: true,
      delete_inspections: false,
      view_all_reports: true,
      create_reports: true,
      edit_all_reports: false,
      manage_users: false,
      manage_roles: false,
      manage_lookups: false,
      view_dashboard_stats: true,
      assign_surveyors: false
    }
  },
  {
    id: 'associate_surveyor',
    name: 'Associate Surveyor',
    icon: User,
    color: 'bg-cyan-500',
    description: 'Limited access to assigned inspections only',
    permissions: {
      view_all_inspections: false,
      create_inspections: false,
      edit_all_inspections: false,
      delete_inspections: false,
      view_all_reports: false,
      create_reports: false,
      edit_all_reports: false,
      manage_users: false,
      manage_roles: false,
      manage_lookups: false,
      view_dashboard_stats: false,
      assign_surveyors: false
    }
  },
  {
    id: 'external_surveyor',
    name: 'External Surveyor',
    icon: Eye,
    color: 'bg-gray-500',
    description: 'Read-only access to assigned inspections',
    permissions: {
      view_all_inspections: false,
      create_inspections: false,
      edit_all_inspections: false,
      delete_inspections: false,
      view_all_reports: false,
      create_reports: false,
      edit_all_reports: false,
      manage_users: false,
      manage_roles: false,
      manage_lookups: false,
      view_dashboard_stats: false,
      assign_surveyors: false
    }
  }
];

const PERMISSION_LABELS = {
  view_all_inspections: 'View All Inspections',
  create_inspections: 'Create Inspections',
  edit_all_inspections: 'Edit All Inspections',
  delete_inspections: 'Delete Inspections',
  view_all_reports: 'View All Reports',
  create_reports: 'Create Reports',
  edit_all_reports: 'Edit All Reports',
  manage_users: 'Manage Users',
  manage_roles: 'Manage Roles',
  manage_lookups: 'Manage Lookups',
  view_dashboard_stats: 'View Dashboard Stats',
  assign_surveyors: 'Assign Surveyors'
};

export default function RoleManagementPage() {
  const { user } = useContext(AuthContext);

  // Check admin access
  const isAdmin = ['director', 'admin'].includes(user?.role);
  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Admin access required</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="role-management-page">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2" data-testid="role-management-title">
            Roles & Permissions
          </h1>
          <p className="text-muted-foreground">
            View role definitions and their associated permissions
          </p>
        </div>

        {/* Role Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ROLES.map((role, index) => (
            <motion.div
              key={role.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="h-full" data-testid={`role-card-${role.id}`}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${role.color}`}>
                      <role.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{role.name}</CardTitle>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {role.id}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {role.description}
                  </p>
                  <div className="text-sm">
                    <span className="font-medium text-primary">
                      {Object.values(role.permissions).filter(Boolean).length}
                    </span>
                    <span className="text-muted-foreground">
                      {' '}/ {Object.keys(role.permissions).length} permissions
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Permissions Matrix */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-500" />
              Permission Matrix
            </CardTitle>
            <CardDescription>
              Detailed view of permissions across all roles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table data-testid="permissions-matrix">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-64">Permission</TableHead>
                    {ROLES.map(role => (
                      <TableHead key={role.id} className="text-center min-w-24">
                        <div className="flex flex-col items-center gap-1">
                          <Badge className={`${role.color} text-white text-xs`}>
                            {role.name}
                          </Badge>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(PERMISSION_LABELS).map(([permKey, permLabel]) => (
                    <TableRow key={permKey} data-testid={`permission-row-${permKey}`}>
                      <TableCell className="font-medium">{permLabel}</TableCell>
                      {ROLES.map(role => (
                        <TableCell key={role.id} className="text-center">
                          {role.permissions[permKey] ? (
                            <Check className="h-5 w-5 text-emerald-500 mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-red-400 mx-auto" />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <Shield className="h-8 w-8 text-amber-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-1">Role-Based Access Control</h3>
                <p className="text-sm text-amber-700">
                  Roles are predefined and cannot be modified through the UI. Each user is assigned one role 
                  that determines their access level throughout the application. Contact your system 
                  administrator if you need to modify role permissions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
